import { type ConditionalDisplayConfig, hiddenTabIds } from "@repo/shared";
import type { FlowField } from "@repo/shared/types";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import type { FlowPageComponent, TabConfig, TabsBlockConfig } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { type ConditionEvaluationData, evaluateCondition } from "./flow-wizard/flow-wizard-utils";

type TabsBlockRendererProps = {
  layoutId: string;
  layoutConfig: string | null;
  components: (FlowPageComponent & { flowField?: FlowField })[];
  renderComponent: (component: FlowPageComponent & { flowField?: FlowField }) => React.ReactNode;
  conditionData: ConditionEvaluationData;
  showValidationErrors?: boolean;
  missingTabKeys?: Set<string>;
};

/**
 * Render tab description if configured
 */
function renderTabDescription(tabConfig: TabConfig | undefined): React.ReactNode {
  if (!tabConfig?.description) {
    return null;
  }

  return <p className="text-sm text-muted-foreground mb-4">{tabConfig.description}</p>;
}

export function TabsBlockRenderer({
  layoutId,
  layoutConfig,
  components,
  renderComponent,
  conditionData,
  showValidationErrors,
  missingTabKeys,
}: TabsBlockRendererProps) {
  // Parse tabs from config
  const config: TabsBlockConfig = (() => {
    try {
      return layoutConfig ? JSON.parse(layoutConfig) : { tabs: [] };
    } catch {
      return { tabs: [] };
    }
  })();

  // QL-217 — a tab whose own rule fails is not offered at all.
  const hidden = hiddenTabIds(config, (rule) => evaluateCondition(rule, conditionData));
  const tabs = (config.tabs || []).filter((tab) => !hidden.has(tab.id));

  // Track the picked tab. If an answer hides it, fall back to the first
  // visible one without forgetting the pick, so it comes back with its tab.
  const [pickedTab, setActiveTab] = useState(tabs[0]?.id || "");
  const activeTab = tabs.some((tab) => tab.id === pickedTab) ? pickedTab : tabs[0]?.id || "";

  // Get components for a specific tab
  const getTabComponents = (tabId: string) => {
    return components.filter((c) => c.tabId === tabId).sort((a, b) => a.order - b.order);
  };

  // Don't render if no tabs
  if (tabs.length === 0) {
    return null;
  }

  // Get components for the active tab
  const activeTabComponents = getTabComponents(activeTab);
  const activeTabConfig = tabs.find((t) => t.id === activeTab);

  return (
    <div className="w-full">
      {/* Tab navigation - matching financial situation widget style */}
      <nav className="flex gap-4 border-b mb-4">
        {tabs.map((tab) => {
          const tabHasError = !!(
            showValidationErrors && missingTabKeys?.has(`${layoutId}:${tab.id}`)
          );
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "pb-2 text-sm font-medium transition-colors relative inline-flex items-center gap-1.5",
                activeTab === tab.id
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.name}
              {tabHasError && <TriangleAlert className="h-3.5 w-3.5 text-destructive" />}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          );
        })}
      </nav>

      {/* Tab content */}
      <div className="space-y-6">
        {renderTabDescription(activeTabConfig)}
        {activeTabComponents.map((component) => {
          // Check conditional display for each component
          let conditionalDisplay: ConditionalDisplayConfig | null = null;
          try {
            const compConfig = component.config ? JSON.parse(component.config) : null;
            conditionalDisplay = compConfig?.conditionalDisplay || null;
          } catch {
            // Invalid config, ignore
          }

          const shouldShow = evaluateCondition(conditionalDisplay, conditionData);

          if (!shouldShow) return null;

          return <div key={component.id}>{renderComponent(component)}</div>;
        })}
      </div>
    </div>
  );
}
